import React, { useMemo, useCallback, useState } from 'react';
import { useFlight } from '../contexts/FlightContext';
import { useAuth } from '../contexts/AuthContext';
import { ARRIVAL_STATIONS, ArrivalStation, TimestampEntry, UserRole } from '../types';
import { CheckIcon, PaperAirplaneIcon, XCircleIcon } from '../components/Icons';

interface ArrivalProgressBarProps {
    canInteract: boolean;
    setClearConfirm: (station: ArrivalStation) => void;
    setRevertConfirm: (station: ArrivalStation) => void;
}

const ArrivalProgressBar: React.FC<ArrivalProgressBarProps> = ({ canInteract, setClearConfirm, setRevertConfirm }) => {
    const { selectedFlightData: flightData } = useFlight();

    const getLatestTimestamp = useCallback((station: ArrivalStation): TimestampEntry | null => {
        const entries = flightData?.arrivalMovement?.[station]?.events;
        return entries && entries.length > 0 ? entries[entries.length - 1] : null;
    }, [flightData]);

    const lastCompletedStationIndex = useMemo(() => {
        let lastConsecutiveIndex = -1;
        for (let i = 0; i < ARRIVAL_STATIONS.length; i++) {
            if (getLatestTimestamp(ARRIVAL_STATIONS[i])) {
                lastConsecutiveIndex = i;
            } else {
                // As soon as we find a station without a timestamp, the consecutive sequence is broken.
                break;
            }
        }
        return lastConsecutiveIndex;
    }, [getLatestTimestamp]);

    if (!flightData) return null;

    return (
        <div className="w-full overflow-x-auto py-8 -mx-4 px-4">
            <div className="relative mt-4">
                <div className="flex items-start" style={{ minWidth: `${ARRIVAL_STATIONS.length * 120}px` }}>
                    {ARRIVAL_STATIONS.map((station, index) => {
                        const timestamp = getLatestTimestamp(station);
                        const isCompleted = index <= lastCompletedStationIndex;
                        const isCurrent = lastCompletedStationIndex === index - 1;
                        const isClickable = isCompleted && canInteract;

                        const nodeClasses = isCompleted
                            ? 'bg-gradient-to-br from-orange-500 to-orange-600 border-orange-400'
                            : 'bg-slate-700 border-slate-600';
                        if (isCurrent) nodeClasses.concat(' animate-glow');

                        const connectorClasses = index <= lastCompletedStationIndex
                            ? 'bg-gradient-to-r from-orange-500 to-orange-600'
                            : 'bg-slate-700';
                        
                        const handleClick = () => {
                            if (!isClickable) return;
                            if (index === lastCompletedStationIndex) {
                                // Action to clear just the last step
                                setClearConfirm(station);
                            } else {
                                // Action to revert progress back to this step
                                setRevertConfirm(station);
                            }
                        };
                        
                        const nodeVisuals = (
                            <div className={`w-10 h-10 rounded-full border-2 flex items-center justify-center relative transition-all duration-300 ${nodeClasses} ${isClickable ? 'group-hover:bg-red-600 group-hover:border-red-400 group-hover:scale-110' : ''}`}>
                                {!isCompleted && null}
                                {isCompleted && (
                                    <>
                                        <CheckIcon className={`w-5 h-5 text-white transition-opacity duration-300 ${isClickable ? 'group-hover:opacity-0' : 'opacity-100'}`} />
                                        {isClickable && (
                                            <XCircleIcon className="w-6 h-6 text-white absolute opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                                        )}
                                    </>
                                )}
                            </div>
                        );

                        return (
                            <React.Fragment key={station}>
                                <div className="relative group flex flex-col items-center w-28 text-center flex-shrink-0">
                                    <div className="relative z-10">
                                       {isClickable ? (
                                            <button 
                                                onClick={handleClick} 
                                                aria-label={`Interact with timestamp for ${station}`}
                                                className="focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:ring-offset-slate-900 rounded-full"
                                            >
                                                {nodeVisuals}
                                            </button>
                                        ) : (
                                            nodeVisuals
                                        )}
                                    </div>
                                    <div className="mt-2">
                                        <h4 className="font-semibold text-xs text-slate-200 leading-tight h-10 flex items-center justify-center">{station}</h4>
                                        <div className="mt-1 text-xs space-y-0.5 min-h-[15px]">
                                            <span className="text-slate-300 font-mono">{timestamp ? new Date(timestamp.time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit'}) : '--:--'}</span>
                                        </div>
                                    </div>
                                </div>
                                {index < ARRIVAL_STATIONS.length - 1 && (
                                    <div className="flex-1 pt-[19px]">
                                        <div className={`h-1.5 w-full transition-colors duration-500 ${connectorClasses}`}></div>
                                    </div>
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};


const ArrivalDashboardScreen: React.FC = () => {
  const { selectedFlightData, updateArrivalMovement, clearArrivalMovement, revertArrivalMovementToStation, updateArrivalData, showNotification } = useFlight();
  const { user } = useAuth();
  const [clearConfirm, setClearConfirm] = useState<ArrivalStation | null>(null);
  const [revertConfirm, setRevertConfirm] = useState<ArrivalStation | null>(null);
  
  const canInteract = useMemo(() => user && [UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.AGENT, UserRole.LL].includes(user.role), [user]);
  
  const handleTimestamp = (station: ArrivalStation) => {
    if (!canInteract) return;
    updateArrivalMovement(station);
    showNotification(`Arrival time for ${station} recorded.`);
  };

  const handleClearTimestamp = () => {
    if (clearConfirm) {
        clearArrivalMovement(clearConfirm);
        showNotification(`Latest arrival time for ${clearConfirm} cleared.`);
        setClearConfirm(null);
    }
  };
  
  const handleRevert = () => {
    if (revertConfirm) {
        revertArrivalMovementToStation(revertConfirm);
        showNotification(`Progress reverted to ${revertConfirm}.`);
        setRevertConfirm(null);
    }
  };

  const handleWheelchairChange = (value: string) => {
      if (!canInteract) return;
      updateArrivalData('wheelchairs', value);
  };

  if (!selectedFlightData) return null;

  if (!selectedFlightData.isReturnFlight) {
    return (
        <div className="text-center p-8 bg-slate-800 rounded-lg max-w-lg mx-auto">
            <h2 className="text-2xl font-bold text-white mb-2">Arrival Dashboard Not Active</h2>
            <p className="text-slate-300">This is not designated as a return flight. The Arrival Dashboard is not applicable.</p>
            <p className="text-xs text-slate-500 mt-4">An administrator can enable this in the Admin Panel.</p>
        </div>
    );
  }

  const arrivalMovement = selectedFlightData.arrivalMovement || { wheelchairs: '' };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h1 className="text-3xl font-bold text-white">Arrival Dashboard</h1>
        <p className="text-lg text-blue-300">Flight {selectedFlightData.counterDistribution.flightNo}</p>
      </div>

      <div className="p-4 bg-slate-900/60 backdrop-blur-md border border-slate-700 rounded-xl shadow-lg">
         <h3 className="text-lg font-bold text-orange-400 border-b border-slate-700 pb-2 mb-4">Arrival Progress</h3>
         <ArrivalProgressBar canInteract={canInteract} setClearConfirm={setClearConfirm} setRevertConfirm={setRevertConfirm} />
         <div className="mt-6 pt-4 border-t border-slate-700 flex justify-center items-center gap-4">
            <label htmlFor="wheelchairs" className="text-lg font-bold text-orange-400">Wheelchairs:</label>
            <input
              id="wheelchairs"
              type="number"
              value={arrivalMovement.wheelchairs || ''}
              onChange={(e) => handleWheelchairChange(e.target.value)}
              className="w-28 text-center text-2xl font-bold bg-slate-800/50 border border-slate-600 rounded-md py-1 px-2 focus:ring-orange-500 focus:border-orange-500"
              placeholder="0"
              disabled={!canInteract}
            />
        </div>
       </div>
      
       <div className="bg-slate-900/60 backdrop-blur-md border border-slate-700 rounded-xl shadow-lg p-4">
        <h3 className="text-lg font-bold text-orange-400 mb-4">Log Arrival Events</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {ARRIVAL_STATIONS.map(station => {
                const timestamp = arrivalMovement[station]?.events?.[arrivalMovement[station]?.events.length - 1];
                return (
                     <div key={station} className="flex flex-col items-center space-y-2">
                        <button 
                            onClick={() => handleTimestamp(station)}
                            disabled={!canInteract}
                            className={`w-16 h-16 flex items-center justify-center rounded-full transition-all duration-200 ${timestamp ? 'bg-orange-600 text-white shadow-[0_0_15px_rgba(249,115,22,0.6)]' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'} disabled:bg-slate-800 disabled:text-slate-500 disabled:cursor-not-allowed`}
                            aria-label={`Record time for ${station}`}
                        >
                            <PaperAirplaneIcon className="w-7 h-7" />
                        </button>
                        <p className="text-xs text-center text-slate-300 h-8">{station}</p>
                        <div className="h-4 text-sm font-mono text-slate-400 flex items-center justify-center gap-1">
                            <span>
                                {timestamp ? new Date(timestamp.time).toLocaleTimeString('en-GB', {hour: '2-digit', minute:'2-digit'}) : '--:--'}
                            </span>
                        </div>
                    </div>
                );
            })}
        </div>
      </div>

        {clearConfirm && (
            <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-slide-up-fade-in">
                <div className="bg-slate-800 rounded-lg shadow-2xl p-6 w-full max-w-md text-center border border-slate-700">
                    <h2 className="text-xl font-bold text-white mb-4">Confirm Clear</h2>
                    <p className="text-slate-300 mb-6">
                        Are you sure you want to clear the latest time for <strong className="text-amber-400">{clearConfirm}</strong>? This action cannot be undone.
                    </p>
                    <div className="flex justify-center gap-4">
                        <button onClick={() => setClearConfirm(null)} className="bg-slate-600 hover:bg-slate-500 text-white font-bold py-2 px-6 rounded-lg">Cancel</button>
                        <button onClick={handleClearTimestamp} className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-6 rounded-lg">Confirm & Clear</button>
                    </div>
                </div>
            </div>
        )}
        
        {revertConfirm && (
            <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-slide-up-fade-in">
                <div className="bg-slate-800 rounded-lg shadow-2xl p-6 w-full max-w-md text-center border border-slate-700">
                    <h2 className="text-xl font-bold text-white mb-4">Confirm Revert</h2>
                    <p className="text-slate-300 mb-6">
                        Are you sure you want to revert progress to this point? All steps after "<strong className="text-amber-400">{revertConfirm}</strong>" will be cleared.
                    </p>
                    <div className="flex justify-center gap-4">
                        <button onClick={() => setRevertConfirm(null)} className="bg-slate-600 hover:bg-slate-500 text-white font-bold py-2 px-6 rounded-lg">Cancel</button>
                        <button onClick={handleRevert} className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-6 rounded-lg">Confirm & Revert</button>
                    </div>
                </div>
            </div>
        )}
    </div>
  );
};

export default ArrivalDashboardScreen;
